/** Splits one CSV line into fields, honouring double-quoted fields (which may contain commas and "" escapes). */
function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        field += '"'
        i += 1
      } else if (char === '"') {
        inQuotes = false
      } else {
        field += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      fields.push(field)
      field = ''
    } else {
      field += char
    }
  }
  return [...fields, field]
}

/** A file's contents as a table, for showing someone what they are about to import. */
export interface ImportPreview {
  columns: string[]
  rows: string[][]
  /** Why the file can't be read as rows at all - the API would reject it too. */
  problem: string | null
}

/**
 * These parsers exist only to preview a file and warn before an import - the
 * API remains the authority on whether it is valid. So the CSV one reads
 * line by line and does not handle a quoted field that spans lines.
 */
export function previewCsv(csv: string): ImportPreview {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim() !== '')
  const header = lines[0]
  if (!header) {
    return { columns: [], rows: [], problem: 'The file is empty.' }
  }
  return {
    columns: splitCsvLine(header).map((name) => name.trim()),
    rows: lines.slice(1).map((line) => splitCsvLine(line).map((value) => value.trim())),
    problem: null,
  }
}

/** Previews a JSON array of flat records, with one column per key seen across them (in first-seen order). */
export function previewJsonArray(json: string): ImportPreview {
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return { columns: [], rows: [], problem: 'The file is not valid JSON.' }
  }
  if (!Array.isArray(parsed)) {
    return { columns: [], rows: [], problem: 'Expected a JSON array of records.' }
  }
  const records = parsed.map((item): Record<string, unknown> => (item && typeof item === 'object' && !Array.isArray(item) ? item : { value: item }))
  const columns = [...new Set(records.flatMap((record) => Object.keys(record)))]
  return {
    columns,
    rows: records.map((record) => columns.map((column) => (record[column] === undefined || record[column] === null ? '' : String(record[column])))),
    problem: null,
  }
}

/** The distinct facilityIds a previewed roster refers to, or null when it has no `facilityId` column to read. */
export function rosterFacilityIds(preview: ImportPreview): string[] | null {
  const column = preview.columns.indexOf('facilityId')
  if (column === -1) {
    return null
  }
  return [...new Set(preview.rows.map((row) => row[column] ?? '').filter((id) => id !== ''))]
}
